import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  User, Mail, Shield, Clock, Check, X, 
  Server, Network, Settings, Monitor, UserCog
} from 'lucide-react';

// Module icons
const moduleIcons = {
  'assets': Server,
  'micro': Monitor,
  'config': Settings,
  'admin': UserCog,
  'core': Shield,
};

// Module display names
const moduleNames = {
  'assets': 'Assets',
  'micro': 'Microinformática',
  'config': 'Configuración',
  'admin': 'Administración',
  'core': 'Core',
};

// Helper to format page name from page_key
const formatPageName = (pageKey) => {
  // Remove module prefix and format nicely
  const parts = pageKey.split('.');
  const pageName = parts.length > 1 ? parts[1] : pageKey;
  // Convert snake_case to Title Case
  return pageName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PermissionBadge = ({ allowed }) => (
  <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${
    allowed 
      ? 'bg-green-500/20 text-green-400' 
      : 'bg-slate-500/20 text-slate-500'
  }`}>
    {allowed ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
  </span>
);

const Profile = () => {
  const { user, permissions, isAdmin } = useAuth();

  // Group permissions by module
  const groupedPermissions = {};
  Object.entries(permissions || {}).forEach(([pageKey, perms]) => {
    const module = pageKey.includes('.') ? pageKey.split('.')[0] : 'core';
    if (!groupedPermissions[module]) {
      groupedPermissions[module] = [];
    }
    groupedPermissions[module].push({ pageKey, ...perms });
  });

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Mi Perfil</h1>
          <p className="page-subtitle">Información de tu cuenta y permisos asignados</p>
        </div>

        {/* User Info Card */}
        <Card className="card-subtle">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-primary" />
              Información de Usuario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-16 w-16 rounded-lg bg-primary/20 flex items-center justify-center">
                    <span className="text-primary text-2xl font-bold">
                      {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {user?.full_name || user?.username}
                    </h3>
                    <p className="text-sm text-muted-foreground">@{user?.username}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Email:</span>
                    <span className="text-foreground">{user?.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Rol:</span>
                    <span className="badge badge-primary">
                      {user?.role?.name || 'Sin rol'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {isAdmin && (
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      <span className="font-semibold text-primary">Administrador</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Tienes acceso completo a todas las funcionalidades del sistema.
                    </p>
                  </div>
                )}
                
                <div className="text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>Permisos en {Object.keys(permissions || {}).length} páginas</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Permissions Matrix */}
        <Card className="card-subtle">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="h-5 w-5 text-primary" />
              Matriz de Permisos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isAdmin ? (
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 text-center">
                <Shield className="h-10 w-10 text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Como administrador, tienes acceso <span className="text-primary font-semibold">CRUD completo</span> a todas las páginas del sistema.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedPermissions).map(([module, pages]) => {
                  const ModuleIcon = moduleIcons[module] || Shield;
                  return (
                    <div key={module}>
                      <div className="flex items-center gap-2 mb-3">
                        <ModuleIcon className="h-4 w-4 text-primary" />
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                          {moduleNames[module] || module}
                        </h4>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border/30">
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Página</th>
                              <th className="text-center py-2 px-3 font-medium text-muted-foreground w-20">Leer</th>
                              <th className="text-center py-2 px-3 font-medium text-muted-foreground w-20">Crear</th>
                              <th className="text-center py-2 px-3 font-medium text-muted-foreground w-20">Editar</th>
                              <th className="text-center py-2 px-3 font-medium text-muted-foreground w-20">Eliminar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pages.map((page) => (
                              <tr key={page.pageKey} className="border-b border-border/20">
                                <td className="py-2 px-3 text-foreground">
                                  {formatPageName(page.pageKey)}
                                </td>
                                <td className="text-center py-2 px-3">
                                  <PermissionBadge allowed={page.read} />
                                </td>
                                <td className="text-center py-2 px-3">
                                  <PermissionBadge allowed={page.create} />
                                </td>
                                <td className="text-center py-2 px-3">
                                  <PermissionBadge allowed={page.update} />
                                </td>
                                <td className="text-center py-2 px-3">
                                  <PermissionBadge allowed={page.delete} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
                
                {Object.keys(groupedPermissions).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No tienes permisos asignados. Contacta al administrador.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Profile;
